/* Passions theme JS — vanilla ES6, no dependencies. */
(function () {
  'use strict';

  var DESKTOP_MIN = 1024;

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait || 150);
    };
  }

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* Shopify.formatMoney-style formatter driven by the shop money format string. */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    /* Legacy shops keep HTML wrappers in the money format; the result is
       written with textContent, so tags would show as literal text. */
    var formatString = (format || '{{amount}}').replace(/<[^>]*>/g, '');
    if (!formatString) formatString = '{{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (number == null || isNaN(number)) return '0';
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return parts[1] ? units + decimal + parts[1] : units;
    }

    var match = formatString.match(placeholderRegex);
    var value;
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount':
      default:
        value = withDelimiters(cents, 2);
    }
    /* Match the server-rendered money_without_trailing_zeros: drop ",00"/".00"
       so a variant change doesn't visibly reformat an unchanged price. */
    value = value.replace(/[.,]00$/, '');
    return formatString.replace(placeholderRegex, value);
  }

  /* ------------------------------------------------------------------ */
  /* Viewport vars: --vh (mobile-safe 100vh) + --header-height           */
  /* ------------------------------------------------------------------ */

  function setViewportVars() {
    var root = document.documentElement;
    root.style.setProperty('--vh', window.innerHeight / 100 + 'px');
    var header = document.getElementById('page-infos');
    if (header) {
      root.style.setProperty('--header-height', header.offsetTop + header.offsetHeight + 'px');
    }
    var mainLogo = document.getElementById('main-logo');
    if (mainLogo) {
      root.style.setProperty('--main-logo-height', mainLogo.offsetTop + mainLogo.offsetHeight + 'px');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Footer mobile menu                                                  */
  /* ------------------------------------------------------------------ */

  function initFooterMenu() {
    document.addEventListener('click', function (event) {
      var toggle = event.target.closest('[data-menu-toggle]');
      var close = event.target.closest('[data-menu-close]');
      if (!toggle && !close) return;
      var footer = (toggle || close).closest('.site-footer') || document.querySelector('.site-footer');
      if (!footer) return;
      if (toggle) footer.classList.toggle('opened');
      else footer.classList.remove('opened');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Shuffle grid: card width = image ratio × 0.86 viewport height       */
  /* ------------------------------------------------------------------ */

  function sizeShuffleGrids() {
    var grids = document.querySelectorAll('[data-shuffle-grid]');
    if (!grids.length) return;
    var desktop = window.innerWidth >= DESKTOP_MIN;
    var cardHeight = 0.86 * window.innerHeight;
    var maxWidth = 0.92 * window.innerWidth;
    grids.forEach(function (grid) {
      grid.querySelectorAll('[data-card]').forEach(function (card) {
        if (!desktop) {
          card.style.width = '';
          return;
        }
        var ratio = parseFloat(card.getAttribute('data-ratio'));
        if (!ratio || isNaN(ratio)) return;
        card.style.width = Math.min(cardHeight * ratio, maxWidth) + 'px';
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Stack slider: random offsets, .is-selected cycling with wrap        */
  /* ------------------------------------------------------------------ */

  function initStackSlider(slider) {
    var slides = Array.prototype.slice.call(slider.querySelectorAll('[data-slide]'));
    if (!slides.length) return;

    slides.forEach(function (slide) {
      var offsets = [];
      for (var i = 0; i < 4; i += 1) offsets.push((Math.random() * 6).toFixed(2) + 'rem');
      slide.style.setProperty('--random-offset', offsets.join(' '));
    });

    var current = slides.findIndex(function (slide) {
      return slide.classList.contains('is-selected');
    });
    if (current < 0) current = 0;

    function select(index) {
      current = (index + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-selected', i === current);
      });
    }
    select(current);

    slider.querySelectorAll('[data-slide-prev]').forEach(function (button) {
      button.addEventListener('click', function () {
        select(current - 1);
      });
    });
    slider.querySelectorAll('[data-slide-next]').forEach(function (button) {
      button.addEventListener('click', function () {
        select(current + 1);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer                                                         */
  /* ------------------------------------------------------------------ */

  function getDrawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  /* Element focused before the drawer opened; focus returns to it on close. */
  var cartReturnFocusEl = null;

  function setCartToggleExpanded(expanded) {
    document.querySelectorAll('[data-cart-toggle]').forEach(function (el) {
      el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function openCart() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (!drawer.classList.contains('is-active')) {
      cartReturnFocusEl = document.activeElement;
    }
    drawer.classList.add('is-active');
    document.documentElement.classList.add('cart-open');
    setCartToggleExpanded(true);
    var closeButton = drawer.querySelector('[data-cart-close]');
    if (closeButton) closeButton.focus();
  }

  function closeCart() {
    var drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('is-active')) return;
    drawer.classList.remove('is-active');
    document.documentElement.classList.remove('cart-open');
    setCartToggleExpanded(false);
    var target =
      cartReturnFocusEl && document.contains(cartReturnFocusEl)
        ? cartReturnFocusEl
        : document.querySelector('[data-cart-toggle]');
    if (target && typeof target.focus === 'function') target.focus();
    cartReturnFocusEl = null;
  }

  function toggleCart() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (drawer.classList.contains('is-active')) closeCart();
    else openCart();
  }

  function initCartDrawer() {
    document.addEventListener('click', function (event) {
      if (event.target.closest('[data-cart-toggle]')) {
        event.preventDefault();
        toggleCart();
        return;
      }
      if (event.target.closest('[data-cart-close]')) {
        event.preventDefault();
        closeCart();
        return;
      }
      // Click outside the drawer closes it.
      var drawer = getDrawer();
      if (drawer && drawer.classList.contains('is-active') && !drawer.contains(event.target)) {
        closeCart();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeCart();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart AJAX                                                           */
  /* ------------------------------------------------------------------ */

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
    document.querySelectorAll('[data-cart-count-wrap]').forEach(function (el) {
      el.classList.toggle('hidden', !count);
    });
  }

  /* On /cart the main-cart section has no AJAX hooks: re-render it too so the
     page beneath the drawer never shows stale quantities/subtotal. */
  function refreshCartPage() {
    var cartPage = document.querySelector('.cart-page');
    if (!cartPage) return Promise.resolve();
    var sectionEl = cartPage.closest('[id^="shopify-section-"]');
    if (!sectionEl) return Promise.resolve();
    var sectionId = sectionEl.id.replace('shopify-section-', '');
    return fetch(shopifyRoot() + 'cart?sections=' + encodeURIComponent(sectionId))
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        var html = sections && sections[sectionId];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('.cart-page');
        if (fresh) cartPage.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  function refreshCart() {
    var root = shopifyRoot();
    return fetch(root + '?sections=cart-drawer')
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        var html = sections && sections['cart-drawer'];
        var target = document.querySelector('[data-cart-items-root]');
        if (html && target) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-items-root]');
          if (fresh) target.innerHTML = fresh.innerHTML;
        }
      })
      .then(refreshCartPage)
      .then(function () {
        return fetch(root + 'cart.js');
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') updateCartCount(cart.item_count);
      })
      .catch(function () {});
  }

  /* Serialize cart mutations: each POST + refresh completes before the next
     starts, so overlapping requests can't drop increments or paint a stale
     drawer out of order. */
  var cartMutationQueue = Promise.resolve();

  function queueCartMutation(task) {
    cartMutationQueue = cartMutationQueue.then(task, task);
    return cartMutationQueue;
  }

  function changeLine(line, quantity) {
    if (!line) return;
    queueCartMutation(function () {
      return fetch(shopifyRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(refreshCart)
        .catch(function () {});
    });
  }

  function initCartAjax() {
    // Add to cart: intercept product form submit.
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[data-product-form]')) return;
      event.preventDefault();

      // In-flight guard: a double click / double Enter must not add twice.
      if (form.hasAttribute('data-busy')) return;
      form.setAttribute('data-busy', '');

      var errorEl = form.querySelector('[data-product-error]');
      var button = form.querySelector('[data-add-to-cart]');
      var body = new FormData(form);
      if (errorEl) errorEl.textContent = '';
      if (button) button.setAttribute('aria-busy', 'true');

      queueCartMutation(function () {
        return fetch(shopifyRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: body
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            if (result.ok) {
              return refreshCart().then(openCart);
            }
            if (errorEl) {
              errorEl.textContent =
                (result.data && (result.data.description || result.data.message)) || '';
            }
            return undefined;
          })
          .catch(function () {})
          .then(function () {
            form.removeAttribute('data-busy');
            if (button) button.removeAttribute('aria-busy');
          });
      });
    });

    // Quantity buttons (delegated so re-rendered drawer content keeps working).
    document.addEventListener('click', function (event) {
      var minus = event.target.closest('[data-cart-qty-minus]');
      var plus = event.target.closest('[data-cart-qty-plus]');
      if (!minus && !plus) return;
      var button = minus || plus;
      var line = parseInt(button.getAttribute('data-line'), 10);
      if (!line) return;
      var input = document.querySelector('[data-cart-qty-input][data-line="' + line + '"]');
      var quantity = input ? parseInt(input.value, 10) || 0 : 1;
      quantity = minus ? Math.max(0, quantity - 1) : quantity + 1;
      // Optimistic write: rapid clicks read the new value, not the stale one.
      if (input) input.value = quantity;
      changeLine(line, quantity);
    });

    // Quantity input direct edit.
    document.addEventListener('change', function (event) {
      var input = event.target.closest && event.target.closest('[data-cart-qty-input]');
      if (!input) return;
      var line = parseInt(input.getAttribute('data-line'), 10);
      if (!line) return;
      changeLine(line, Math.max(0, parseInt(input.value, 10) || 0));
    });

    // Remove line (delegated).
    document.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      var line = parseInt(remove.getAttribute('data-line'), 10);
      if (!line) return;
      changeLine(line, 0);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Product variant logic                                               */
  /* ------------------------------------------------------------------ */

  function variantOptions(variant) {
    if (variant.options) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter(function (value) {
      return value != null;
    });
  }

  function onVariantChange(select) {
    var form = select.closest('form[data-product-form]');
    var scope = form || select.closest('section') || document;
    var jsonEl =
      scope.querySelector('script[data-product-json]') ||
      document.querySelector('script[data-product-json]');
    if (!jsonEl) return;

    var variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(variants)) return;

    var selects = Array.prototype.slice.call(scope.querySelectorAll('[data-variant-select]'));
    var values = selects.map(function (el) {
      return el.value;
    });

    var variant = variants.find(function (candidate) {
      var options = variantOptions(candidate);
      return values.every(function (value, index) {
        return options[index] === value;
      });
    });

    if (form) {
      var idInput = form.querySelector('input[name="id"]');
      if (idInput && variant) idInput.value = variant.id;
    }

    var button = scope.querySelector('[data-add-to-cart]');
    if (button) {
      var available = !!(variant && variant.available);
      button.disabled = !available;
      var text = available
        ? button.getAttribute('data-text-add')
        : button.getAttribute('data-text-soldout');
      if (text) button.textContent = text;
    }

    if (variant && typeof variant.price !== 'undefined') {
      var format = (form && form.getAttribute('data-money-format')) || '';
      var priceScope = (form && form.closest('.shopify-section')) || document;
      priceScope.querySelectorAll('[data-buy-price]').forEach(function (el) {
        el.textContent = formatMoney(variant.price, format);
      });
    }
  }

  function initVariantSelects() {
    document.addEventListener('change', function (event) {
      var select = event.target.closest && event.target.closest('[data-variant-select]');
      if (select) onVariantChange(select);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    setViewportVars();
    sizeShuffleGrids();
    document.querySelectorAll('[data-stack-slider]').forEach(initStackSlider);

    initFooterMenu();
    initCartDrawer();
    initCartAjax();
    initVariantSelects();

    window.addEventListener(
      'resize',
      debounce(function () {
        setViewportVars();
        sizeShuffleGrids();
      }, 150)
    );
    // Re-measure once assets/fonts have loaded (header height can shift).
    window.addEventListener('load', setViewportVars);

    // Theme editor: a settings tweak re-renders the section's DOM, so sliders
    // need fresh listeners/offsets and grid cards fresh inline widths.
    document.addEventListener('shopify:section:load', function (event) {
      var scope = event.target || document;
      setViewportVars();
      sizeShuffleGrids();
      scope.querySelectorAll('[data-stack-slider]').forEach(initStackSlider);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
